import IAuthorizable from 'common/models/IAuthorizable';
import IResolverService from 'core/service/resolver/IResolverService';
import Logger from 'core/util/Logger';
import ServiceRegistry from "core/util/ServiceRegistry";
import intersection from 'lodash/intersection';
import ConfigUtil from '../config/ConfigUtil';
import StringUtil from '../StringUtil';
import TypeUtil, { iDef } from "../TypeUtils";
import UrlUtil from "../UrlUtil";
import AbstractTokenSession from "./AbstractTokenSession";
import TokenContext from './TokenContext';
import { Institution, TokenUser } from "./TokenUser";

const INIT_CONTEXT_QUERY = `
query {
	context_current {
		id
		publicId
		name
		locale
		timeZone
	}
}
`;
const INIT_USER_QUERY = `
query {
	userCurrent(params: {}) {
		id
		firstName
		lastName
		email
		createDate
		locale
		lastLoginDate
		roles {
			id
			name
		}
		groups {
			id
			name
		}
	}
	institutionsUser(params: {pageSize: 0}) {
		id
		guid
		name
	} 
}
`;

export default class TokenSession extends AbstractTokenSession {
	protected user: TokenUser;
	protected context: TokenContext;

	private static getInstance() {
		if (!TokenSession.instance) {
			TokenSession.instance = new TokenSession();
		}
		return TokenSession.instance;
	}

	private static instance: TokenSession;

	private constructor(token?: string) {
		super(token);
	}

	protected setUser(user: TokenUser) {
		this.user = user;
	}

	protected setContext(context: TokenContext) {
		this.context = context;
	}

	public static init(success: () => void) {
		const session = this.getInstance();

		if (!session.isActive()) {
			TokenSession.getAnonymousToken();
		} else {
			let anonymous = true;
			try {
				const decodedToken = JSON.parse(atob(session.getToken().split('.')[1]));
				if(decodedToken.userId) {
					anonymous = false;
				}
			} catch (e) {}

			const required = [
				// TODO use root query class
				ServiceRegistry.get(IResolverService).execute(INIT_CONTEXT_QUERY).then((res) => {
					if (res && res.data && res.data.context_current) {
						session.setContext(TypeUtil.become(res.data.context_current, TokenContext));
						Logger.debug("Succesfully fetched TokenSession context details");
					} else {
						TokenSession.initFailure();
					}
				})
			];

			if(!anonymous) {
				// TODO use root query class
				required.push(ServiceRegistry.get(IResolverService).execute(INIT_USER_QUERY).then((res) => {
					if (res && res.data && res.data.userCurrent) {
						let user:TokenUser = TypeUtil.become(res.data.userCurrent, TokenUser);
						if(res.data.institutionsUser) {
							if(Array.isArray(res.data.institutionsUser)){
								if(res.data.institutionsUser.length>0){
									user.addInstitutions(...res.data.institutionsUser);
								}
							}else{
								user.addInstitutions(res.data.institutionsUser);
							}
						}
					
						session.setUser(user);
						Logger.debug("Succesfully fetched TokenSession user details");
					} else {
						TokenSession.initFailure();
					}
				}));
			} else {
				session.setUser(new TokenUser());
			}

			Promise.all(required)
				.then(success)
				.catch(TokenSession.initFailure);
		}

			/*
			ServiceRegistry.getQueryService().findOne(TokenUser, {}).then((result: any) => {

				if(result && result.data) {
					session.setUser(TypeUtil.become(result.data.entity, TokenUser));

					Logger.debug("Succesfully fetched TokenSession user details");

					// execute the success callback and do not procede
				} else {
					TokenSession.initFailure();
				}
			}).then(success).catch(TokenSession.initFailure);
			*/
		
	}

	private static getAnonymousToken(){
		
			const tokenUrl = ConfigUtil.getEnvConfig().tokenRefreshUri;
			const xhr = new XMLHttpRequest();
			//remove existing token from url
			let url = UrlUtil.removeUrlParam(window.location.href, "token");
			const queryParam= url.split('?')[1];
			const entryParams=(queryParam != null || queryParam != undefined) ? ("&"+queryParam) : '';
			//Get Domain from URL
			const domainAlias: string = UrlUtil.extractDomain();
			const body = { domainAlias: domainAlias };
			//Incase no Or incorrect URL redirect to login page
			if (!domainAlias) TokenSession.initFailure();
			xhr.onreadystatechange = function() {
					if (xhr.readyState == 4) {
						if (xhr.status == 200) {
							let token = xhr.responseText;
							//set is token
							if (iDef(token) && !StringUtil.isEmpty(token)) {
								document.location.replace(
									document.location.origin +
										document.location.pathname +
										"?token=" +
										token + entryParams
								);
							} else {
								TokenSession.initFailure();
							}
						} else {
							TokenSession.initFailure();
						}
					}
			};

			xhr.open("POST", tokenUrl + "getPublic", true); // true for asynchronous
			xhr.setRequestHeader("Content-Type", "application/json");

			xhr.send(JSON.stringify(body));
	
	}

	private static initFailure(e = null) {
		Logger.error("Failed to fetch token user");
		// console.log(e);

		// kick back to the the lms gateway for a new token
		TokenSession.refresh();
	}

	public static getToken(): string {
		return TokenSession.getInstance().getToken();
	}

	public static getUserId(): number {
		return TokenSession.getInstance().user.getId();
	}

	public static hasRoles(...roles: string[]): boolean {
		if(!TokenSession.getInstance().user) return false;
		const common = intersection(TokenSession.getInstance().user.getRoles(), roles);
		return common && common.length > 0;
	}

	public static hasGroups(...groups: string[]): boolean {
		if(!TokenSession.getInstance().user) return false;
		const common = intersection(TokenSession.getInstance().user.getGroups(), groups);
		// console.log(TokenSession.getInstance().user.getGroups());
		return common && common.length > 0;
	}

	public static getLocale(): string | null {
		return TokenSession.getUser() && TokenSession.getUser().locale || TokenSession.getContext().locale || "en_US";
	}

	public static getUser(): TokenUser {
		return TokenSession.getInstance().user;
	}

	public static getContext(): TokenContext {
		return TokenSession.getInstance().context;
	}

	public static editingEnabled() {
		return TokenSession.hasRoles("ROLE_CONTEXT_EDITOR");
	}

	public static getUserGroups(): string[] {
		return TokenSession.getInstance().user.getGroups();
	}

	public static getUserRoles(): string[] {
		return TokenSession.getInstance().user.getRoles();
	}

	public static getUserInstitutions(): Institution[] {
		return TokenSession.getInstance().user.getInstitutions();
	}

	public static isAuthenticated(): boolean {
		return TokenSession.getInstance().user && TokenSession.getInstance().user.getId() > 0;
	}

	/**
	 * check permission against object (see panel configs or nav menu links)
	 */
	public static isAuthorized(o: IAuthorizable): boolean {
		const { user } = TokenSession.getInstance();

		const groups = o["__groupsAllowed"];
		const roles = o["__rolesAllowed"];

		return (
			(!groups || groups.length < 1 || user.getGroups().some(groups.includes))

			&&

			(!roles || roles.length < 1 || user.getRoles().some(roles.includes))
		);
	}

	public static getEstablished(): number {
		return TokenSession.getInstance().established;
	}

	/**
	 * Attempts to establish a wicket session using the current token.
	 * If successufl execute the callback, if not, redirect to login
	 *
	 * @param callback
	 */
	public static doWithWicketSession(callback: () => void) {
		fetch(UrlUtil.getAuthorizedLink(TokenSession.getToken(), "_health"), {
			credentials: 'include', // <- this is mandatory to deal with cookies
		})
			.then((response) => response.text())
			.then( (data) => {
				if(data && data.trim() === "ok") {
					callback();
				} else {
					throw new Error("bad response");
				}
			})
			.catch((e) => {
				Logger.error("Failed to establish wicket session: %s", e);
				TokenSession.refresh();
			});
	}
}



// WEBPACK FOOTER //
// ./src/core/util/session/TokenSession.ts